import React, { useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";

import {
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  createMuiTheme,
  ThemeProvider,
  makeStyles,
} from "@material-ui/core/styles";
import classNames from "classnames";
// import CityDataPDF from "../../assets/CitiIQ_Global_4000_Jan_2023.pdf";
import CityDataPDF from "../../assets/Global 4000 City Download 2025.pdf";

const FormSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("Required field")
    .max(20, "Must be 20 characters or less"),
  lastName: Yup.string()
    .required("Required field")
    .max(20, "Must be 20 characters or less"),
  emailAddress: Yup.string()
    .required("Required field")
    .email("Invalid email address"),
  title: Yup.string()
    .required("Required field")
    .max(100, "Must be 100 characters or less"),
  phoneNumber: Yup.string().max(15, "Must be 15 characters or less"),
});

const fields = [
  {
    required: true,
    placeholder: "First Name * ",
    name: "firstName",
    isHalfWidth: true,
  },
  {
    required: true,
    placeholder: "Last Name * ",
    name: "lastName",
    isHalfWidth: true,
  },
  {
    required: true,
    placeholder: "Email Address * ",
    name: "emailAddress",
  },
  {
    required: true,
    placeholder: "Title * ",
    name: "title",
  },
  {
    required: true,
    placeholder: "Phone Number",
    name: "phoneNumber",
  },
];

const initialValues = {
  firstName: "",
  lastName: "",
  emailAddress: "",
  title: "",
  phoneNumber: "",
};

const useStyles = makeStyles({
  form: {
    clear: "both",
    padding: "30px 20px",
  },
  button: {
    padding: "7px 25px",
    border: 0,
    borderRadius: "6px",
    background: "#9EC73C !important",
    transition: ".3s ease all",
    marginLeft: "10px !important",
    marginRight: "10px !important",
    width: "calc(100% - 20px)",
    "&:hover": {
      opacity: 0.8,
    },
  },
  link: {
    color: "#9EC73C",
    fontWeight: 500,
    textDecoration: "underline",
    cursor: "pointer",
    transition: ".3s ease all",
    "&:hover": {
      opacity: 0.8,
    },
  },
  input: {
    width: "100%",
    float: "left",
    color: "white",
    padding: "0 10px",
    boxSizing: "border-box",
  },
  loader: {
    color: "#9EC73C",
  },
  inputHalfWidth: {
    width: "50%",
  },
  thankYouNote: {
    textAlign: "center",
    color: "#fff",
  },
});

const theme = createMuiTheme({
  palette: {
    primary: {
      main: "#9EC73C",
    },
    secondary: {
      main: "#ffffff",
    },
  },
  overrides: {
    MuiFormLabel: {
      root: {
        color: "rgba(255,255,255,.5)",
      },
    },
    MuiOutlinedInput: {
      color: "rgba(255,255,255,.7)",
      notchedOutline: {
        borderColor: "rgba(255,255,255,.3)",
      },
      root: {
        "&:hover:not($disabled):not($focused):not($error) $notchedOutline": {
          borderColor: "rgba(255,255,255,.7)",
        },
      },
    },
  },
});

export default function DownloadForm({ onComplete }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const sendEmail = (data) => {
    const { emailAddress, firstName, lastName, phoneNumber, title } = data;
    const emailObject = {
      personalizations: [
        {
          to: [
            {
              email: "info@citiiq.com",
              name: firstName,
            },
          ],
          dynamic_template_data: {
            user_phone: phoneNumber || "N/A",
            user_first_name: firstName,
            user_last_name: lastName,
            user_email: emailAddress,
            user_title: title,
          },
        },
      ],
      from: {
        email: "noreply@CitiIQ.com",
        name: "CitiIQ",
      },

      template_id: "d-c7dbcac1d47d4f8da8f34301de03b26b",
    };

    axios({
      method: "post",
      url: `https://b2svh8ioo3.execute-api.us-east-1.amazonaws.com/production/send`,
      data: emailObject,
    });
  };

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      setIsSubmitting(false);
      downloadFile(CityDataPDF, "CitiIQ_Global4000_PDF");
      setIsCompleted(true);
      sendEmail(data);
    } catch {
      setIsSubmitting(false);
      alert("Error in submitting form. Please try again later");
      onComplete();
    }
  };
  const classes = useStyles();

  return (
    <ThemeProvider theme={theme}>
      {isCompleted && (
        <>
          <Typography variant="body1" className={classes.thankYouNote}>
            Thank you! <br />
            <br />
            If download did not start automatically, please right click{" "}
            <a
              href={CityDataPDF}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              this link
            </a>{" "}
            to manually download the data.
            <br />
            <br />
          </Typography>
          <Button className={classes.button} onClick={() => onComplete()}>
            Back to map
          </Button>
        </>
      )}
      {isSubmitting && (
        <div className={classes.loader}>
          <CircularProgress color="inherit" />
        </div>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={FormSchema}
        onSubmit={handleSubmit}
      >
        {!isSubmitting &&
          !isCompleted &&
          (({ errors, touched, isSubmitting, isValid }) => (
            <Form id="form" className={classes.form}>
              {fields.map(({ required, placeholder, name, isHalfWidth }) => (
                <div
                  className={classNames(classes.input, {
                    [classes.inputHalfWidth]: isHalfWidth,
                  })}
                >
                  <Field name={name} key={name}>
                    {({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        margin="normal"
                        label={placeholder}
                        variant="outlined"
                        InputProps={{
                          className: classes.input,
                        }}
                        error={
                          errors[name] && touched[name] ? !!errors[name] : false
                        }
                        helperText={
                          errors[name] && touched[name] ? errors[name] : null
                        }
                      />
                    )}
                  </Field>
                </div>
              ))}
              <Button
                fullWidth
                color="secondary"
                size="large"
                variant="contained"
                disabled={isSubmitting || !isValid}
                type="submit"
                className={classes.button}
                style={{ margin: "15px 0 20px" }}
              >
                Submit and Download PDF
              </Button>
            </Form>
          ))}
      </Formik>
    </ThemeProvider>
  );
}

function downloadFile(fileURL, fileName) {
  // for non-IE
  if (!window.ActiveXObject) {
    var save = document.createElement("a");
    save.href = fileURL;
    save.target = "_blank";
    var filename = fileURL.substring(fileURL.lastIndexOf("/") + 1);
    save.download = fileName || filename;
    if (
      navigator.userAgent.toLowerCase().match(/(ipad|iphone|safari)/) &&
      navigator.userAgent.search("Chrome") < 0
    ) {
      document.location = save.href;
      // window event not working here
    } else {
      var evt = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: false,
      });
      save.dispatchEvent(evt);
      (window.URL || window.webkitURL).revokeObjectURL(save.href);
    }
  }
  // for IE < 11
  else if (!!window.ActiveXObject && document.execCommand) {
    var _window = window.open(fileURL, "_blank");
    _window.document.close();
    _window.document.execCommand("SaveAs", true, fileName || fileURL);
    _window.close();
  }
}
